var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { jsx as _jsx } from "react/jsx-runtime";
import { boundMethod } from 'autobind-decorator';
import isEqual from 'lodash/isEqual';
import memoize from 'memoize-one';
import { PureComponent } from 'react';
import { ajaxSend } from 'api/api';
import { addAgentsToQueue, getQueueAgents, removeQueueAgents, updateQueueAgents, } from './api/queueAgents';
const DEFAULT_FETCH_THROTTLE = 300;
export const withQueueAgents = (Component, settings = {}) => {
    class WithQueueAgents extends PureComponent {
        constructor(props) {
            super(props);
            this.timeout = null;
            this.state = {
                items: [],
                searchTerm: '',
                sortBy: 'display_name:asc',
                nextPagePath: null,
                previousPagePath: null,
                getItemsAsyncStatus: 'succeeded',
                addAsyncStatus: 'succeeded',
                updateAsyncStatus: 'succeeded',
                removeAsyncStatus: 'succeeded',
            };
            this.memoizedGetInjectedProps = memoize(this.memoizedGetInjectedProps, isEqual).bind(this); // We have to bind it like this. Using @boundMethod won't work
        }
        componentWillUnmount() {
            if (this.timeout) {
                clearTimeout(this.timeout);
            }
        }
        getQueueAgents(path) {
            this.setState({ getItemsAsyncStatus: 'loading' });
            const req = path
                ? ajaxSend({ path })
                : getQueueAgents(this.props.queueId, {
                    search: this.state.searchTerm,
                    limit: settings.pageSize,
                    sort_by: !this.state.searchTerm ? this.state.sortBy : '', // We don't support sort and search at the same time,
                });
            return req.then(this.handleQueryResolve).catch(this.handleQueryReject);
        }
        nextPage() {
            const { nextPagePath } = this.state;
            if (nextPagePath) {
                return this.getQueueAgents(nextPagePath);
            }
            throw new Error('Next page does not exist');
        }
        previousPage() {
            const { previousPagePath } = this.state;
            if (previousPagePath) {
                return this.getQueueAgents(previousPagePath);
            }
            throw new Error('Previous page does not exist');
        }
        add(agentIds) {
            this.setState({ addAsyncStatus: 'loading' });
            return addAgentsToQueue(this.props.queueId, agentIds)
                .then((response) => {
                this.setState({ addAsyncStatus: 'succeeded' });
                return response;
            })
                .catch((err) => {
                this.setState({ addAsyncStatus: 'failed' });
                throw err;
            });
        }
        update(agents) {
            this.setState({ updateAsyncStatus: 'loading' });
            return updateQueueAgents(this.props.queueId, agents)
                .then((response) => {
                this.setState({ updateAsyncStatus: 'succeeded' });
                return response;
            })
                .catch((err) => {
                this.setState({ updateAsyncStatus: 'failed' });
                throw err;
            });
        }
        remove(agentIds) {
            this.setState({ removeAsyncStatus: 'loading' });
            return removeQueueAgents(this.props.queueId, agentIds)
                .then((response) => {
                this.setState({ removeAsyncStatus: 'succeeded' });
                return response;
            })
                .catch((err) => {
                this.setState({ removeAsyncStatus: 'failed' });
                throw err;
            });
        }
        handleSearchTermChange(searchTerm = '', fetchThrottle = DEFAULT_FETCH_THROTTLE) {
            return new Promise((resolve) => {
                this.setState({ searchTerm }, () => {
                    if (fetchThrottle >= 0) {
                        if (this.timeout) {
                            clearTimeout(this.timeout);
                        }
                        this.timeout = setTimeout(() => {
                            resolve(this.getQueueAgents());
                        }, fetchThrottle);
                    }
                    else {
                        resolve();
                    }
                });
            });
        }
        handleSortByChange(sortBy, direction = 'asc', refetch = true) {
            return new Promise((resolve, reject) => {
                this.setState({
                    sortBy: `${sortBy}:${direction}`,
                }, () => {
                    if (refetch) {
                        resolve(this.getQueueAgents());
                    }
                    else {
                        resolve();
                    }
                });
            });
        }
        handleQueryResolve(response) {
            this.setState({
                getItemsAsyncStatus: 'succeeded',
                items: response.data.agents,
                nextPagePath: response.meta.next_page,
                previousPagePath: response.meta.prev_page,
            });
            return response;
        }
        handleQueryReject(error) {
            this.setState({
                getItemsAsyncStatus: 'failed',
            });
            return error;
        }
        getInjectedProps(props, state) {
            const { items, searchTerm, sortBy, nextPagePath, previousPagePath, getItemsAsyncStatus, addAsyncStatus, updateAsyncStatus, removeAsyncStatus, } = state;
            return this.memoizedGetInjectedProps(searchTerm, sortBy, items, getItemsAsyncStatus, addAsyncStatus, updateAsyncStatus, removeAsyncStatus, nextPagePath, previousPagePath);
        }
        memoizedGetInjectedProps(searchTerm, sortBy, items, getItemsAsyncStatus, addAsyncStatus, updateAsyncStatus, removeAsyncStatus, nextPagePath, previousPagePath) {
            return {
                searchTerm: searchTerm,
                sortBy: sortBy,
                items: items,
                getItemsAsyncStatus: getItemsAsyncStatus,
                addAsyncStatus: addAsyncStatus,
                updateAsyncStatus: updateAsyncStatus,
                removeAsyncStatus: removeAsyncStatus,
                hasNextPage: nextPagePath !== null,
                hasPreviousPage: previousPagePath !== null,
                getItems: this.getQueueAgents,
                searchTermChange: this.handleSearchTermChange,
                sortByChange: this.handleSortByChange,
                nextPage: this.nextPage,
                previousPage: this.previousPage,
                add: this.add,
                update: this.update,
                remove: this.remove,
            };
        }
        render() {
            return (_jsx(Component, Object.assign({}, this.props, { queueAgents: this.getInjectedProps(this.props, this.state) })));
        }
    }
    __decorate([
        boundMethod
    ], WithQueueAgents.prototype, "getQueueAgents", null);
    __decorate([
        boundMethod
    ], WithQueueAgents.prototype, "nextPage", null);
    __decorate([
        boundMethod
    ], WithQueueAgents.prototype, "previousPage", null);
    __decorate([
        boundMethod
    ], WithQueueAgents.prototype, "add", null);
    __decorate([
        boundMethod
    ], WithQueueAgents.prototype, "update", null);
    __decorate([
        boundMethod
    ], WithQueueAgents.prototype, "remove", null);
    __decorate([
        boundMethod
    ], WithQueueAgents.prototype, "handleSearchTermChange", null);
    __decorate([
        boundMethod
    ], WithQueueAgents.prototype, "handleSortByChange", null);
    __decorate([
        boundMethod
    ], WithQueueAgents.prototype, "handleQueryResolve", null);
    __decorate([
        boundMethod
    ], WithQueueAgents.prototype, "handleQueryReject", null);
    __decorate([
        boundMethod
    ], WithQueueAgents.prototype, "getInjectedProps", null);
    return WithQueueAgents;
};
